import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Table,
  Tag,
  Typography,
  Space,
  Modal,
  Popconfirm,
  FloatButton,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  PrinterOutlined,
  DeleteOutlined,
  HomeOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import Layout from '../../layout/Layout';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';

const { Text, Title } = Typography;

const STATUS_COLORS = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
  converted: 'blue',
  expired: 'default',
};

export default function QuotationHistory() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [viewItems, setViewItems] = useState([]);

  // Check if user is cashier
  const userType = localStorage.getItem('usertype') || sessionStorage.getItem('usertype');
  const isCashier = userType === 'Cashier';

  const navigateToDashboard = () => {
    if (isCashier) {
      navigate('/CashierDashboard');
    } else {
      navigate('/dashboard');
    }
  };

  // Helper function to convert BLOB to base64
  const blobToBase64 = (blob, mimeType) => {
    if (!blob) return null;
    try {
      let uint8Array;
      if (blob.data) {
        uint8Array = new Uint8Array(blob.data);
      } else if (Array.isArray(blob)) {
        uint8Array = new Uint8Array(blob);
      } else {
        uint8Array = new Uint8Array(blob);
      }
      const base64 = btoa(String.fromCharCode.apply(null, uint8Array));
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error('Error converting BLOB to base64:', error);
      return null;
    }
  };

  // Fetch quotations on component mount
  useEffect(() => {
    fetchQuotations();
    fetchCompanyProfile();
  }, []);

  // Filter quotations based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredQuotations(quotations);
    } else {
      const filtered = quotations.filter(quotation =>
        quotation.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quotation.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quotation.customer_phone?.includes(searchTerm)
      );
      setFilteredQuotations(filtered);
    }
  }, [searchTerm, quotations]);

  const fetchCompanyProfile = async () => {
    try {
      const response = await fetchData('company_profile', null, 'id', {});
      if (response && response.length > 0) {
        setCompanyProfile(response[0]);
      } else {
        console.warn('No company profile data found');
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
      toast.error('Failed to load company profile');
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await fetchData('quotations', null, 'id', {});
      if (Array.isArray(response)) {
        // Sort by creation date (newest first)
        const sortedQuotations = response.sort((a, b) => new Date(b.setup_date) - new Date(a.setup_date));
        setQuotations(sortedQuotations);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Error loading quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (quotation) => {
    // Navigate to quotation page with edit data
    navigate('/quotation', {
      state: {
        editQuotation: quotation,
        mode: 'edit'
      }
    });
  };

  const handleDelete = async (quotationId) => {
    try {
      // Delete related quotation items first (a 404 just means there were none)
      try {
        await axios.delete(`/deletebyid/quotation_items/order_id/${quotationId}`, getHeaders());
      } catch (itemsErr) {
        if (itemsErr.response?.status !== 404) throw itemsErr;
      }
      await axios.delete(`/deletebyid/quotations/id/${quotationId}`, getHeaders());

      toast.success('Quotation deleted successfully');
      fetchQuotations(); // Refresh the list
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast.error('Error deleting quotation');
    }
  };

  const fetchQuotationItemsFor = async (quotation) => {
    const itemsResponse = await fetchData('quotation_items', null, 'id', { order_id: quotation.id });
    return Array.isArray(itemsResponse) ? itemsResponse : [];
  };

  const handleView = async (quotation) => {
    try {
      const items = await fetchQuotationItemsFor(quotation);
      setSelectedQuotation(quotation);
      setViewItems(items);
      setViewModal(true);
    } catch (error) {
      console.error('Error loading quotation items:', error);
      toast.error('Error loading quotation items');
    }
  };

  const handlePrint = async (quotation) => {
    try {
      // Fetch quotation items for this quotation using fetchData
      const items = await fetchQuotationItemsFor(quotation);

      // Prepare quotation data for printing
      const quotationData = {
        ...quotation,
        quotation_number: quotation.quotation_number || `Q${quotation.id}`,
        items: items.map(item => ({
          item_name: item.item_name,
          item_details: item.item_details || item.description,
          quantity: item.quantity,
          rate: item.rate,
          discount_percent: item.discount_percent,
          total_price: item.total_price,
          total_amount: item.total_amount
        }))
      };

      printQuotation(quotationData);
    } catch (error) {
      console.error('Error preparing quotation for print:', error);
      toast.error('Error preparing quotation for printing');
    }
  };

  // Print Quotation Function
  const printQuotation = (lastSavedQuotation) => {
    if (!lastSavedQuotation) {
      toast.error('No quotation data available for printing. Please save the quotation first.');
      return;
    }

    if (!companyProfile) {
      toast.error('Company profile not loaded. Please try again.');
      return;
    }

    // Use quotation_number for display, fallback to id if quotation_number is not available
    const quotationNumber = lastSavedQuotation.quotation_number || lastSavedQuotation.id || 'DRAFT';

    // Convert logo to base64 if available
    const logoSrc = companyProfile?.logo && companyProfile?.logo_type
      ? blobToBase64(companyProfile.logo, companyProfile.logo_type)
      : '/logo.png';

    const printWindow = window.open('', '_blank');
   printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Quotation - ${quotationNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
            background: white;
            font-size: 11px;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
            border: 1px solid #ddd;
          }
          .header-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 15px 20px;
            border-bottom: 1px solid #ddd;
          }
          .company-info {
            display: flex;
            gap: 15px;
            align-items: center;
          }
          .logo {
            width: 80px;
            height: 80px;
            object-fit: contain;
          }
          .company-details h2 {
            color: #FF6B35;
            font-size: 16px;
            margin-bottom: 3px;
          }
          .company-details p {
            font-size: 9px;
            color: #666;
            line-height: 1.3;
            margin: 2px 0;
          }
          .header-contact {
            text-align: right;
            font-size: 9px;
            color: #666;
            line-height: 1.5;
          }
          .title-bar {
            background: #FF6B35;
            color: black;
            text-align: center;
            padding: 8px;
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 2px;
          }
          .info-section {
            display: flex;
            border-bottom: 1px solid #ddd;
          }
          .invoice-info, .customer-info {
            flex: 1;
            padding: 12px 20px;
          }
          .invoice-info {
            border-right: 1px solid #ddd;
          }
          .info-section h4 {
            font-size: 10px;
            color: #333;
            margin-bottom: 8px;
            padding-bottom: 3px;
            border-bottom: 1px solid #eee;
          }
          .info-row {
            display: flex;
            font-size: 9px;
            margin: 4px 0;
          }
          .info-label {
            font-weight: 600;
            min-width: 90px;
            color: #333;
          }
          .info-value {
            color: #555;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
          }
          .items-table th {
            background: #666;
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-size: 10px;
            font-weight: 600;
          }
          .items-table td {
            padding: 10px 10px;
            border-bottom: 1px solid #eee;
            font-size: 10px;
            vertical-align: top;
          }
          .items-table tr:last-child td {
            border-bottom: none;
          }
          .item-description {
            font-weight: 600;
            color: #0066cc;
            margin-bottom: 4px;
          }
          .item-details {
            font-size: 9px;
            color: #666;
            font-style: italic;
            line-height: 1.4;
            margin-top: 4px;
          }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 120px;
            color: rgba(200, 200, 200, 0.15);
            font-weight: bold;
            z-index: -1;
            pointer-events: none;
          }
          .summary-section {
            border-top: 2px solid #333;
            padding: 0;
          }
          .summary-table {
            width: 100%;
            border-collapse: collapse;
          }
          .summary-row {
            display: flex;
            justify-content: flex-end;
            border-bottom: 1px solid #eee;
          }
          .summary-content {
            width: 300px;
            display: flex;
            justify-content: space-between;
            padding: 6px 20px;
            font-size: 10px;
          }
          .summary-label {
            color: #555;
          }
          .summary-value {
            font-weight: 600;
            color: #333;
          }
          .grand-total-row {
            background: #FF6B35;
            color: white;
            border: none;
          }
          .grand-total-row .summary-content {
            font-size: 12px;
            font-weight: bold;
          }
          .footer-section {
            display: flex;
            padding: 15px 20px;
            gap: 20px;
          }
          .terms-section {
            flex: 1;
          }
          .terms-section h4 {
            color: #FF6B35;
            font-size: 10px;
            margin-bottom: 5px;
          }
          .terms-section p {
            font-size: 8px;
            color: #666;
            line-height: 1.4;
            margin: 3px 0;
          }
          .bank-section {
            width: 280px;
            border: 1px solid #ddd;
            padding: 10px;
            background: #f9f9f9;
          }
          .bank-section h4 {
            color: #333;
            font-size: 10px;
            margin-bottom: 8px;
            padding-bottom: 3px;
            border-bottom: 1px solid #ddd;
          }
          .bank-row {
            display: flex;
            font-size: 9px;
            margin: 3px 0;
          }
          .bank-label {
            font-weight: 600;
            min-width: 90px;
            color: #333;
          }
          .bank-value {
            color: #555;
          }
          .signature-section {
            border-top: 1px solid #ddd;
            padding: 15px 20px;
            text-align: right;
          }
          .signature-box {
            display: inline-block;
            text-align: center;
            min-width: 200px;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 50px;
            padding-top: 5px;
            font-size: 10px;
            font-weight: 600;
            color: #333;
          }
          .footer-note {
            text-align: center;
            padding: 8px;
            background: #f5f5f5;
            font-size: 8px;
            color: #666;
            font-style: italic;
          }
          @media print {
            body { padding: 0; }
            .container { border: none; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header-section">
            <div class="company-info">
              <img src="${logoSrc}" alt="Logo" class="logo" onerror="this.style.display='none'" />
              <div class="company-details">
                <h2>${companyProfile.name || 'Company Name'}</h2>
                <p><strong>Tax ID:</strong> ${companyProfile.tax_id || companyProfile.taxid || '0000000000000'}</p>
                <p>${companyProfile.address || ''}</p>
                <p>${companyProfile.city ? companyProfile.city + ', ' : ''}${companyProfile.state || ''} ${companyProfile.zipcode || ''}</p>
              </div>
            </div>
            <div class="header-contact">
              <p>📞 ${companyProfile.phone_number || ''}</p>
              <p>🌐 ${companyProfile.website || 'https://www.cloudnetsoftwares.com'}</p>
              <p>📧 ${companyProfile.email || 'cloudnetsoftwares@gmail.com'}</p>
            </div>
          </div>

          <!-- Title Bar -->
          <div class="title-bar">QUOTATION</div>

          <!-- Invoice & Customer Info -->
          <div class="info-section">
            <div class="invoice-info">
              <h4>📋 Quotation Information</h4>
              <div class="info-row">
                <span class="info-label">Quotation Number:</span>
                <span class="info-value">${quotationNumber}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Quotation Date:</span>
                <span class="info-value">${new Date(lastSavedQuotation.setup_date).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Valid Until:</span>
                <span class="info-value">${new Date(lastSavedQuotation.valid_until).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">${lastSavedQuotation.payment_mode || 'Cash'}</span>
              </div>
            </div>
            <div class="customer-info">
              <h4>👤 Customer Details</h4>
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${lastSavedQuotation.customer_name || 'Walk-in Customer'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone:</span>
                <span class="info-value">${lastSavedQuotation.customer_phone || 'N/A'}</span>
              </div>
              ${lastSavedQuotation.customer_email ? `<div class="info-row">
                <span class="info-label">Email:</span>
                <span class="info-value">${lastSavedQuotation.customer_email}</span>
              </div>` : ''}
              ${lastSavedQuotation.customer_address ? `<div class="info-row">
                <span class="info-label">Address:</span>
                <span class="info-value">${lastSavedQuotation.customer_address}</span>
              </div>` : ''}
            </div>
          </div>

          <!-- Items Table -->
          <table class="items-table">
            <thead>
              <tr>
                <th width="5%" class="text-center">#</th>
                <th width="40%">Item Description</th>
                <th width="12%" class="text-right">Unit Price</th>
                <th width="8%" class="text-center">Qty</th>
                <th width="12%" class="text-right">Amount</th>
                <th width="10%" class="text-right">Disc%</th>
                <th width="13%" class="text-right">Net Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lastSavedQuotation.items && lastSavedQuotation.items.length > 0 ? lastSavedQuotation.items.map((item, index) => `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>
                    <div class="item-description">${item.item_name || 'N/A'}</div>
                    ${item.item_details ? `<div class="item-details">${item.item_details}</div>` : ''}
                  </td>
                  <td class="text-right">฿ ${parseFloat(item.rate || 0).toFixed(2)}</td>
                  <td class="text-center">${parseFloat(item.quantity || 0)}</td>
                  <td class="text-right">฿ ${(parseFloat(item.rate || 0) * parseFloat(item.quantity || 1)).toFixed(2)}</td>
                  <td class="text-right">${+parseFloat(item.discount_percent || 0).toFixed(2)}%</td>
                  <td class="text-right">฿ ${parseFloat(item.total_price || 0).toFixed(2)}</td>
                </tr>
              `).join('') : '<tr><td colspan="7" class="text-center" style="padding: 20px;">No items found</td></tr>'}
            </tbody>
          </table>

          <!-- Page Break Space for Items -->
          <div style="min-height: 250px;"></div>

          <!-- Summary Section -->
          <div class="summary-section">
            <div class="summary-row">
              <div class="summary-content">
                <span class="summary-label">Subtotal:</span>
                <span class="summary-value">฿ ${parseFloat(lastSavedQuotation.subtotal || 0).toFixed(2)}</span>
              </div>
            </div>
            <div class="summary-row">
              <div class="summary-content">
                <span class="summary-label">Discount:</span>
                <span class="summary-value">- ฿ ${(parseFloat(lastSavedQuotation.subtotal || 0) - parseFloat(lastSavedQuotation.subtotal_afterdiscount || 0)).toFixed(2)}</span>
              </div>
            </div>
            <div class="summary-row grand-total-row">
              <div class="summary-content">
                <span class="summary-label">GRAND TOTAL:</span>
                <span class="summary-value">฿ ${parseFloat(lastSavedQuotation.grand_total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <!-- Footer Section with QR Code in Middle -->
          <div style="display: flex; padding: 15px 20px; gap: 15px; align-items: flex-start;">
            <div style="flex: 1;">
              <h4 style="color: #FF6B35; font-size: 10px; margin-bottom: 5px;">Terms & Conditions:</h4>
              ${companyProfile.terms_and_conditions ? companyProfile.terms_and_conditions.split('\n').map(line => `<p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">${line}</p>`).join('') : `
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Payment is due upon receipt of invoice</p>
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Late payments may incur additional charges</p>
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Company liability limited to product value</p>
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Customer responsible for order accuracy</p>
              `}
              <p style="margin-top: 8px; font-size: 8px; color: #666; font-style: italic;">For support, contact: ${companyProfile.email || 'support@cloudnetsoftwares.com'}</p>
            </div>

            ${companyProfile.qr_code && companyProfile.qr_code_type ? `
            <div style="text-align: center; padding: 0 15px;">
              <img src="${blobToBase64(companyProfile.qr_code, companyProfile.qr_code_type)}"
                   alt="QR Code"
                   style="max-width: 120px; max-height: 120px; object-fit: contain;"
                   onerror="this.style.display='none'" />
            </div>
            ` : ''}

            <div style="width: 280px; border: 1px solid #ddd; padding: 10px; background: #f9f9f9;">
              <h4 style="color: #333; font-size: 10px; margin-bottom: 8px; padding-bottom: 3px; border-bottom: 1px solid #ddd;">💳 Bank Details</h4>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Bank Name:</span>
                <span style="color: #555;">${companyProfile.bank_name || 'Siam Commercial Bank'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Account Name:</span>
                <span style="color: #555;">${companyProfile.account_name || companyProfile.name || 'Cloudnet Softwares Co., Ltd.'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Account Number:</span>
                <span style="color: #555;">${companyProfile.account_number || '000-000-0000'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Routing Number:</span>
                <span style="color: #555;">${companyProfile.routing_number || '0'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">SWIFT Code:</span>
                <span style="color: #555;">${companyProfile.swift_code || '0'}</span>
              </div>
            </div>
          </div>

          <!-- Signature -->
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">Authorized Signatory</div>
            </div>
          </div>

          <!-- Footer Note -->
          <div class="footer-note">
            This is a computer-generated quotation and does not require a physical signature.
          </div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // Auto print after a short delay
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const quotationColumns = [
    {
      title: 'Quotation No',
      dataIndex: 'quotation_number',
      key: 'quotation_number',
      render: (v, record) => <Text strong>{v || `Q${record.id}`}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'setup_date',
      key: 'setup_date',
      sorter: (a, b) => new Date(a.setup_date || 0) - new Date(b.setup_date || 0),
      render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : 'N/A'),
    },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', render: (v) => v || 'N/A' },
    { title: 'Phone', dataIndex: 'customer_phone', key: 'customer_phone', render: (v) => v || 'N/A' },
    {
      title: 'Valid Until',
      dataIndex: 'valid_until',
      key: 'valid_until',
      render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : 'N/A'),
    },
    {
      title: 'Grand Total',
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      sorter: (a, b) => (Number(a.grand_total) || 0) - (Number(b.grand_total) || 0),
      render: (v) => <Text strong style={{ color: '#0d9488' }}>{`฿${(Number(v) || 0).toFixed(2)}`}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{(v || 'pending').toUpperCase()}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, quotation) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(quotation)} title="View" />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(quotation)} title="Edit Quotation" />
          <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(quotation)} title="Print Quotation" />
          <Popconfirm
            title="Delete this quotation?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(quotation.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} title="Delete Quotation" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const itemColumns = [
    { title: '#', key: 'sno', width: 50, align: 'center', render: (_, __, index) => index + 1 },
    {
      title: 'Item Description',
      key: 'item_name',
      render: (_, item) => (
        <div>
          <Text strong style={{ color: '#0066cc' }}>{item.item_name || 'N/A'}</Text>
          {item.item_details && (
            <div>
              <Text type="secondary" italic style={{ fontSize: 12 }}>{item.item_details}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'rate',
      key: 'rate',
      align: 'right',
      render: (v) => `฿${parseFloat(v || 0).toFixed(2)}`,
    },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', align: 'center' },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      render: (_, item) => `฿${(parseFloat(item.rate || 0) * parseFloat(item.quantity || 1)).toFixed(2)}`,
    },
    { title: 'Disc%', key: 'disc', align: 'right', render: () => '0%' },
    {
      title: 'Net Amount',
      dataIndex: 'total_price',
      key: 'total_price',
      align: 'right',
      render: (v) => <Text strong style={{ color: '#0d9488' }}>{`฿${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
  ];

  return (
    <Layout>
      <ToastContainer />
      {isCashier && (
        <FloatButton
          icon={<HomeOutlined />}
          tooltip="Dashboard"
          onClick={navigateToDashboard}
          style={{ insetInlineEnd: 24 }}
        />
      )}

      <Card
        style={{ marginBottom: 16, borderRadius: 12, background: '#f0fdfa', border: '1px solid #99f6e4' }}
        styles={{ body: { padding: 16 } }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 12]}>
          <Col xs={24} md={12}>
            <Space align="center">
              <FileTextOutlined style={{ color: '#0d9488', fontSize: 18 }} />
              <Title level={5} style={{ margin: 0 }}>Quotation History</Title>
            </Space>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/quotation')}>
              New Quotation
            </Button>
          </Col>
        </Row>

        <Row style={{ marginTop: 12 }} gutter={[16, 12]} align="middle" justify="space-between">
          <Col xs={24} md={12}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#0d9488' }} />}
              placeholder="Search by customer name, quotation number, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {filteredQuotations.length} quotation{filteredQuotations.length !== 1 ? 's' : ''} found
            </Text>
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="id"
          loading={loading}
          columns={quotationColumns}
          dataSource={filteredQuotations}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [10, 25, 50, 100] }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: searchTerm ? 'No quotations found matching your search.' : 'No quotations found.' }}
        />
      </Card>

      <Modal
        title={`Quotation Details - ${selectedQuotation?.quotation_number || `Q${selectedQuotation?.id || ''}`}`}
        open={viewModal}
        onCancel={() => setViewModal(false)}
        width={950}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)}>Close</Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => selectedQuotation && handlePrint(selectedQuotation)}>
            Print
          </Button>,
        ]}
        destroyOnClose
      >
        {selectedQuotation && (
          <>
            <Card
              style={{ marginBottom: 16, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}
              styles={{ body: { padding: 14 } }}
            >
              <Row gutter={[16, 8]}>
                <Col xs={24} md={12}>
                  <p style={{ margin: 0 }}><strong>Customer:</strong> {selectedQuotation.customer_name || 'N/A'}</p>
                  <p style={{ margin: 0 }}><strong>Phone:</strong> {selectedQuotation.customer_phone || 'N/A'}</p>
                  <p style={{ margin: 0 }}><strong>Email:</strong> {selectedQuotation.customer_email || 'N/A'}</p>
                </Col>
                <Col xs={24} md={12}>
                  <p style={{ margin: 0 }}><strong>Date:</strong> {selectedQuotation.setup_date ? dayjs(selectedQuotation.setup_date).format('DD-MM-YYYY') : 'N/A'}</p>
                  <p style={{ margin: 0 }}><strong>Valid Until:</strong> {selectedQuotation.valid_until ? dayjs(selectedQuotation.valid_until).format('DD-MM-YYYY') : 'N/A'}</p>
                  <p style={{ margin: 0 }}>
                    <strong>Grand Total:</strong>{' '}
                    <Text strong style={{ color: '#0d9488' }}>
                      {`฿${(Number(selectedQuotation.grand_total) || 0).toFixed(2)}`}
                    </Text>
                  </p>
                </Col>
              </Row>
            </Card>

            <Table
              rowKey="id"
              columns={itemColumns}
              dataSource={viewItems}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No items found' }}
            />
          </>
        )}
      </Modal>
    </Layout>
  );
}
